import { type FC, useEffect } from "react";
import { type IntlShape, useIntl } from "react-intl";
import { useToken } from "@packsize/authentication";

import { baseRoutes } from "@/api/base-routes";
import { useSignalRHub } from "@/hooks/use-signalr-hub";
import type { ErrorsAndWarningsProps, MachineFaultDisplay, MachineFaultType } from "@/schemas";

const websocketUrl = `${baseRoutes.x4}/hubs/x4machines`;
const errorsChangedEventName = "OnErrorsUpdated";
const warningsChangedEventName = "OnWarningsUpdated";
const subscribeWarningsMethods = "SubscribeWarnings";
const subscribeErrorsMethods = "SubscribeErrors";
const unsubscribeErrorsMethod = "UnsubscribeErrors";
const unsubscribeWarningsMethod = "UnsubscribeWarnings";

type AlarmData = {
	alarm?: number;
	alarmNumber?: number;
	code?: number;
};

function createDisplayItems(
	machineId: string,
	data: AlarmData[] | null | undefined,
	severity: MachineFaultType,
	intl: IntlShape,
): MachineFaultDisplay[] {
	if (!data) {
		return [];
	}

	return data.map((alarm) => {
		const code = alarm.alarm ?? alarm.alarmNumber ?? alarm.code ?? 0;
		const translationKey = `X4 ${severity} ${code}`;

		return {
			machineId,
			title: intl.formatMessage({ id: translationKey, defaultMessage: "{translationKey}" }, { translationKey }),
			image: `${import.meta.env?.VITE_BASENAME}/images/errors-and-warnings/x4/${severity.toLowerCase()}/${code}.svg`,
			fallbackImage: `${import.meta.env?.VITE_BASENAME}/images/errors-and-warnings/x4/${severity.toLowerCase()}/default.svg`,
			type: severity,
		};
	});
}

interface X4MachineErrorsAndWarningsProps {
	readonly machineId: string;
	readonly onErrorsChanged: ErrorsAndWarningsProps["onErrorsChanged"];
	readonly onWarningsChanged: NonNullable<ErrorsAndWarningsProps["onWarningsChanged"]>;
}

const X4MachineErrorsAndWarnings = ({
	machineId,
	onErrorsChanged,
	onWarningsChanged,
}: X4MachineErrorsAndWarningsProps) => {
	const intl = useIntl();
	const { bearerToken } = useToken();
	const { hubConnection, hubConnectionEstablished, subscribe, invoke, unsubscribe } = useSignalRHub({
		hubUrl: websocketUrl,
		token: bearerToken,
	});

	useEffect(() => {
		if (hubConnectionEstablished === false) {
			return;
		}

		const handleErrorsChanged = (...args: unknown[]) => {
			const [_tenantId, eventMachineId, errors] = args as [string, string, AlarmData[] | null | undefined];

			if (eventMachineId !== machineId) {
				return;
			}

			onErrorsChanged(machineId, createDisplayItems(machineId, errors, "Error", intl));
		};

		subscribe(errorsChangedEventName, handleErrorsChanged);
		invoke(subscribeErrorsMethods, machineId);

		return () => {
			unsubscribe(errorsChangedEventName, handleErrorsChanged);
			invoke(unsubscribeErrorsMethod, machineId);
		};
	}, [hubConnection, hubConnectionEstablished, intl, invoke, machineId, onErrorsChanged, subscribe, unsubscribe]);

	useEffect(() => {
		if (hubConnectionEstablished === false) {
			return;
		}

		const handleWarningsChanged = (...args: unknown[]) => {
			const [_tenantId, eventMachineId, warnings] = args as [string, string, AlarmData[] | null | undefined];

			if (eventMachineId !== machineId) {
				return;
			}

			onWarningsChanged(machineId, createDisplayItems(machineId, warnings, "Warning", intl));
		};

		subscribe(warningsChangedEventName, handleWarningsChanged);
		invoke(subscribeWarningsMethods, machineId);

		return () => {
			unsubscribe(warningsChangedEventName, handleWarningsChanged);
			invoke(unsubscribeWarningsMethod, machineId);
		};
	}, [hubConnection, hubConnectionEstablished, intl, invoke, machineId, onWarningsChanged, subscribe, unsubscribe]);

	return null;
};

export const X4ErrorsAndWarnings: FC<Required<ErrorsAndWarningsProps>> = ({
	machines,
	onErrorsChanged,
	onWarningsChanged,
}) => {
	return (
		<>
			{machines
				.filter(({ machineType: configuredMachineType }) => configuredMachineType === "X4")
				.map(({ machineId }) => (
					<X4MachineErrorsAndWarnings
						key={machineId}
						machineId={machineId}
						onErrorsChanged={onErrorsChanged}
						onWarningsChanged={onWarningsChanged}
					/>
				))}
		</>
	);
};
